<template>
  <div class="create-material-set">
    <div class="form">
      <div class="form__item">
        <CustomInput
          label="Название"
          :required="true"
          placeholder="Введите название набора"
          :value="setName || ''"
          @change_input="value => setName = value"
        />
      </div>
      <div class="form__item">
        <CustomInputDropdown
          label="Поиск материала"
          placeholder="Поиск"
          :value="materialSearchValue"
          :options="materialOptions"
          optionValueKey="material_id"
          optionLabelKey="label"
          :loading="materialSearchLoading"
          @change_input="onMaterialSearch"
          @select_item="onMaterialSelect"
        />
      </div>

      <div v-if="currentSelectedMaterial" class="current-material-form">
        <div class="material-name">
          {{ currentSelectedMaterial.material_name }}
        </div>
        <div class="material-inputs">
          <div class="input-row">
            <CustomInput
              label="Расход"
              :required="true"
              type="number"
              :value="currentConsumption"
              @change_input="(val) => (currentConsumption = val)"
            />
          </div>
          <div class="input-row">
            <CustomInput
              label="Формула"
              :required="true"
              :value="currentFormula"
              @change_input="(val) => (currentFormula = val)"
            />
          </div>
        </div>

        <div class="add-material-section">
          <q-btn
            color="primary"
            label="Добавить материал"
            @click="addMaterialToList"
            :disable="!currentConsumption || !currentFormula"
          />
        </div>
      </div>

      <div v-if="selectedMaterials.length > 0" class="selected-materials">
        <div
          v-for="(material, index) in selectedMaterials"
          :key="material.material_id + '_' + index"
          class="material-item"
        >
          <div class="material-info">
            <div class="material-header">
              <div class="material-name">{{ material.material_name }}</div>
            </div>
            <div class="material-details">
              <span>Расход: {{ material.consumption }}</span>
              <span>Формула: {{ material.formula }}</span>
            </div>
            <div class="material-main">
              <CustomCheckbox
                :value="material.is_main"
                @change="setMainMaterial(index)"
                label="Основной"
              />
            </div>
            <div class="material-actions">
              <q-btn
                flat
                round
                dense
                icon="delete"
                color="negative"
                @click="removeMaterial(index)"
              />
            </div>
          </div>
        </div>
      </div>

      <CustomModalFooter
        :btn-loading="loading"
        :on-submit="onSubmit"
        :disable-submit="!canSubmit"
        submit-btn-text="Сохранить"
        :on-cancel="closeModal"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import CustomInputDropdown from '@/components/form/CustomInputDropdown'
import CustomInput from '@/components/form/CustomInput'
import CustomCheckbox from '@/components/form/CustomCheckbox'
import CustomModalFooter from '@/components/modal/modals/modal-footer'

import { useCustomSnackbar } from '@/_helpers/hooks/useCustomSnackbar'
import {
  searchMaterials,
  createMaterialSet,
} from '@/views/PackageEntry/services'

const props = defineProps({
  data: {
    type: Object,
    default: () => ({})
  },
  closeModal: {
    type: Function,
    default: () => {}
  }
})

const { showSucceedSnackbar } = useCustomSnackbar()

const loading = ref(false)
const setName = ref(props.data?.data?.work_set_name || '')
const materialSearchLoading = ref(false)
const materialSearchValue = ref('')
const materialOptions = ref([])
const currentSelectedMaterial = ref(null)
const currentConsumption = ref(null)
const currentFormula = ref('')
const selectedMaterials = ref([])

const workSetId = computed(() => props.data?.params?.workSetId)

const canSubmit = computed(() => {
  if(!setName.value) return false

  const hasValidMaterials = selectedMaterials.value.length > 0 &&
    selectedMaterials.value.every((m) => m.consumption && m.formula)

  return hasValidMaterials
})

const onMaterialSearch = async (searchValue) => {
  materialSearchValue.value = searchValue
  if (!searchValue || searchValue.length < 2) {
    materialOptions.value = []
    return
  }

  materialSearchLoading.value = true
  const res = await searchMaterials(searchValue)
  if (!res) {
    materialSearchLoading.value = false
    return
  }
  materialOptions.value = res || []
  materialSearchLoading.value = false
}

const onMaterialSelect = (_value, material) => {
  if (!material) return
  currentSelectedMaterial.value = material
  materialSearchValue.value = material.material_name
  materialOptions.value = []

  currentConsumption.value = null
  currentFormula.value = ''
}

const addMaterialToList = () => {
  const isFirstMaterial = selectedMaterials.value.length === 0

  selectedMaterials.value.push({
    ...currentSelectedMaterial.value,
    consumption: currentConsumption.value,
    formula: currentFormula.value,
    is_main: isFirstMaterial
  })

  currentSelectedMaterial.value = null
  materialSearchValue.value = ''
  currentConsumption.value = null
  currentFormula.value = ''
}

const removeMaterial = (index) => {
  const removedMaterial = selectedMaterials.value[index]
  selectedMaterials.value.splice(index, 1)

  if (removedMaterial.is_main && selectedMaterials.value.length > 0) {
    selectedMaterials.value[0].is_main = true
  }
}

const setMainMaterial = (index) => {
  selectedMaterials.value.forEach((material, i) => {
    material.is_main = i === index
  })
}

const resetForm = () => {
  selectedMaterials.value = []
  currentSelectedMaterial.value = null
  materialSearchValue.value = ''
  materialOptions.value = []
  currentConsumption.value = null
  currentFormula.value = ''
}

const closeModal = () => {
  resetForm()
  props.closeModal()
}

const onSubmit = async () => {
  if (!canSubmit.value) return

  loading.value = true

  const body = {
    data: selectedMaterials.value.map((m) => ({
      material_id: m.material_id,
      consumption: Number(m.consumption),
      formula: m.formula,
      is_main: m.is_main,
      set_name: setName.value
    }))
  }

  const res = await createMaterialSet(body)
  if (!res) {
    loading.value = false
    return
  }
  loading.value = false

  showSucceedSnackbar('Набор успешно создан')
  const itemsSnapshot = [...selectedMaterials.value]
  resetForm()


  if (props.data?.onClose) {
    closeModal()
    props.data.onClose(res?.material_set_info, itemsSnapshot, setName.value)
    return
  }

  closeModal()
}
</script>

<style scoped lang="scss">
.form {
  display: flex;
  flex-direction: column;
  gap: 15px;

  &__item {
    display: flex;
    flex-direction: column;
  }
}

.current-material-form {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.selected-materials {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.material-item {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 16px;
}

.material-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: relative;
}

.material-header {
  display: flex;
  align-items: center;
}

.material-name {
  font-weight: 500;
  font-size: 14px;
  color: #333;
  width: 90%;
}

.material-main {
  display: flex;
  align-items: center;
  margin-top: 8px;
}

.material-inputs {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.material-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: #666;
}

.input-row {
  display: flex;
  flex-direction: column;
}

.material-actions {
  position: absolute;
  top: 0;
  right: 0;
}

.add-material-section {
  display: flex;
  justify-content: center;
  padding: 16px 0;
}
</style>
